import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Stack,
    Avatar,
    Link,
    Skeleton,
} from '@mui/material';
import { CloseRounded, OpenInNewRounded } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import tasksService from '../../services/tasks.service';
import { DASH, TASK_TONES, avatarTint, getInitials } from './dashboardTokens';
import { Eyebrow, EmptyState } from './Surface';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const formatMoment = (value, language) => {
    if (!value) return null;
    const locale = DAYJS_LOCALE[language] || 'sk';
    return dayjs(value).locale(locale).format('D. M. YYYY, H:mm');
};

const formatDay = (value, language) => {
    if (!value) return null;
    const locale = DAYJS_LOCALE[language] || 'sk';
    return dayjs(value).locale(locale).format('D. M. YYYY');
};

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Riadok „štítok — hodnota“. Bez ikon; štítok nesie význam sám. */
const MetaRow = ({ label, children }) => (
    <Stack
        direction="row"
        spacing={2}
        sx={{
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            alignItems: 'baseline',
        }}
    >
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: 116, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ minWidth: 0, flex: 1, fontSize: '0.875rem' }}>{children}</Box>
    </Stack>
);

/**
 * Priložené zadanie. Vypisuje sa ako text v monospace — markdown sa v admin
 * paneli nerenderuje zámerne: zadanie píše človek alebo agent pre riešiteľa
 * a zamestnávateľ ho tu má prečítať, nie konzumovať naformátované.
 */
const AttachmentBlock = ({ attachment, t }) => {
    const [open, setOpen] = useState(false);
    const content = attachment.content_text || '';
    const lines = content.split('\n');
    const preview = open ? content : lines.slice(0, 12).join('\n');
    const clipped = !open && lines.length > 12;

    return (
        <Box
            sx={{
                borderRadius: `${DASH.radiusSm}px`,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                mb: 1.5,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1.75, py: 1.25, bgcolor: 'action.hover' }}
            >
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, minWidth: 0 }} noWrap>
                    {attachment.file_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatSize(attachment.size_bytes)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {attachment.url && (
                    <Link
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                        aria-label={t('pages.dashboard.tasks.detail.openFile')}
                    >
                        <OpenInNewRounded sx={{ fontSize: 17 }} />
                    </Link>
                )}
            </Stack>

            {content ? (
                <Box sx={{ px: 1.75, py: 1.5 }}>
                    <Typography
                        component="pre"
                        sx={{
                            m: 0,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            fontSize: '0.8125rem',
                            lineHeight: 1.55,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {preview}
                    </Typography>
                    {(clipped || open) && (
                        <Link
                            component="button"
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            sx={{ mt: 1, fontSize: '0.8125rem' }}
                        >
                            {open
                                ? t('pages.dashboard.tasks.detail.showLess')
                                : t('pages.dashboard.tasks.detail.showMore')}
                        </Link>
                    )}
                    {attachment.truncated && open && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t('pages.dashboard.tasks.detail.truncated')}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.75, py: 1.5 }}>
                    {t('pages.dashboard.tasks.detail.contentUnavailable')}
                </Typography>
            )}
        </Box>
    );
};

/**
 * Detail úlohy pre zamestnávateľa — na čítanie, nie na riadenie. Meniť stav
 * úlohy patrí tomu, kto ju rieši, takže tu žiadne akcie nie sú.
 */
const TaskDetailDrawer = ({ taskId, open, onClose, language, t }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!open || !taskId) {
            setDetail(null);
            setFailed(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        tasksService.getTaskDetails(taskId)
            .then((res) => { if (!cancelled) setDetail(res.data); })
            .catch((err) => {
                console.error('Failed to fetch task details', err);
                if (!cancelled) setFailed(true);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, taskId]);

    const status = detail?.status || 'pending';
    const overdue = detail?.due_date
        && status !== 'completed'
        && status !== 'cancelled'
        && dayjs(detail.due_date).isBefore(dayjs());
    const tone = overdue ? TASK_TONES.overdue : (TASK_TONES[status] || TASK_TONES.pending);
    const place = [detail?.conversation_name, detail?.thread_name].filter(Boolean).join(' · ');

    return (
        <Drawer anchor="right" open={open} onClose={onClose} keepMounted={false}>
            <Box sx={{ width: { xs: '100vw', sm: 540 }, p: { xs: 2.5, sm: 3.5 }, height: '100%', overflowY: 'auto' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Eyebrow>{t('pages.dashboard.tasks.detail.title')}</Eyebrow>
                    <IconButton onClick={onClose} size="small" aria-label={t('common.close')}>
                        <CloseRounded />
                    </IconButton>
                </Stack>

                {loading ? (
                    <Box>
                        <Skeleton variant="text" height={34} width="80%" />
                        <Skeleton variant="text" height={34} width="55%" />
                        <Skeleton variant="rounded" height={120} sx={{ mt: 3 }} />
                    </Box>
                ) : failed || !detail ? (
                    <EmptyState
                        title={t('pages.dashboard.tasks.detail.errorTitle')}
                        hint={t('pages.dashboard.tasks.detail.errorHint')}
                    />
                ) : (
                    <>
                        {/* Znenie úlohy je to najdôležitejšie — najväčšie písmo, žiadna karta. */}
                        <Typography
                            sx={{
                                fontFamily: DASH.display,
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.3,
                            }}
                        >
                            {detail.description}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, mb: 3 }}>
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: tone, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 650, color: tone }}>
                                {t(`pages.dashboard.tasks.statuses.${status}`)}
                            </Typography>
                            {place && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {`· ${place}`}
                                </Typography>
                            )}
                        </Stack>

                        <Box>
                            <MetaRow label={t('pages.dashboard.tasks.detail.assignee')}>
                                {detail.assigned_to_name ? (
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Avatar
                                            sx={{
                                                width: 26,
                                                height: 26,
                                                fontSize: '0.6875rem',
                                                fontWeight: 700,
                                                color: '#fff',
                                                bgcolor: avatarTint(detail.assigned_to_name),
                                            }}
                                        >
                                            {getInitials(detail.assigned_to_name)}
                                        </Avatar>
                                        <span>{detail.assigned_to_name}</span>
                                    </Stack>
                                ) : (
                                    <Box component="span" sx={{ color: 'text.secondary' }}>
                                        {t('pages.dashboard.tasks.unassigned')}
                                    </Box>
                                )}
                            </MetaRow>

                            {detail.created_by_name && (
                                <MetaRow label={t('pages.dashboard.tasks.detail.createdBy')}>
                                    {detail.created_by_name}
                                </MetaRow>
                            )}

                            <MetaRow label={t('pages.dashboard.tasks.detail.created')}>
                                {formatMoment(detail.created_at, language)}
                            </MetaRow>

                            {detail.due_date && (
                                <MetaRow label={t('pages.dashboard.tasks.detail.dueDate')}>
                                    <Box component="span" sx={{ color: overdue ? TASK_TONES.overdue : 'inherit', fontWeight: overdue ? 650 : 400 }}>
                                        {formatDay(detail.due_date, language)}
                                    </Box>
                                </MetaRow>
                            )}

                            <MetaRow label={t('pages.dashboard.tasks.detail.priority')}>
                                {t(`pages.dashboard.tasks.priorities.${detail.priority || 'medium'}`)}
                            </MetaRow>

                            {status === 'completed' && (
                                <MetaRow label={t('pages.dashboard.tasks.detail.completed')}>
                                    {formatMoment(detail.updated_at, language)}
                                </MetaRow>
                            )}
                        </Box>

                        {detail.notes && (
                            <Box sx={{ mt: 3.5 }}>
                                <Eyebrow sx={{ display: 'block', mb: 1.25 }}>
                                    {t('pages.dashboard.tasks.detail.notes')}
                                </Eyebrow>
                                <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                    {detail.notes}
                                </Typography>
                            </Box>
                        )}

                        {detail.attachments?.length > 0 && (
                            <Box sx={{ mt: 3.5 }}>
                                <Eyebrow sx={{ display: 'block', mb: 1.25 }}>
                                    {t('pages.dashboard.tasks.detail.attachments')}
                                </Eyebrow>
                                {detail.attachments.map((attachment) => (
                                    <AttachmentBlock key={attachment.id} attachment={attachment} t={t} />
                                ))}
                            </Box>
                        )}

                        {detail.recordings?.length > 0 && (
                            <Box sx={{ mt: 3.5 }}>
                                <Eyebrow sx={{ display: 'block', mb: 1.25 }}>
                                    {t('pages.dashboard.tasks.detail.recordings')}
                                </Eyebrow>
                                {detail.recordings.map((recording) => (
                                    <Box key={recording.id} sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }} noWrap>
                                            {recording.file_name}
                                        </Typography>
                                        {recording.summary && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {recording.summary}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default TaskDetailDrawer;
